import {useEffect, useState} from 'react';
import {graphql} from 'react-relay';
import {useForecastFetchQuery} from '../../../hooks/useForecastFetchQuery';
import {
	deliverableTaskGlobalIds,
	derivePhaseDeliverablePreflight,
	PhaseDeliverablePreflight,
	PreflightAsset,
	PreflightTask,
} from './phaseDeliverablePreflight';
import type {usePhaseDeliverablePreflightAssetsQuery} from './__generated__/usePhaseDeliverablePreflightAssetsQuery.graphql';
import type {usePhaseDeliverablePreflightTasksQuery} from './__generated__/usePhaseDeliverablePreflightTasksQuery.graphql';

// project(internalId:) because $projectId is the project's Relay global id; project(id:)
// expects a customProjectId string and would silently resolve to null.
const assetsQuery = graphql`
	query usePhaseDeliverablePreflightAssetsQuery($projectId: ID!) {
		viewer {
			project(internalId: $projectId) {
				clientAssets {
					id
					assetType
					name
					status
					archived
					psaTaskId
				}
			}
		}
	}
`;

// Only the tasks that carry a deliverable, so the read is bounded by the project's
// deliverables rather than by its task count.
const tasksQuery = graphql`
	query usePhaseDeliverablePreflightTasksQuery($taskIds: [ID], $first: Int) {
		viewer {
			tasks(taskIds: $taskIds, first: $first) {
				edges {
					node {
						id
						phase {
							id
						}
					}
				}
			}
		}
	}
`;

export type PhaseDeliverablePreflightResult = {
	/** Null until the approval state has been read — while loading, or when it could not be. */
	preflight: PhaseDeliverablePreflight | null;
	loading: boolean;
	/**
	 * The approval state could not be read: a fetch failed, or the project's assets came
	 * back unavailable. Kept apart from `preflight` so a failed read is never presented as
	 * "this phase has no deliverables".
	 */
	error: boolean;
};

const IDLE: PhaseDeliverablePreflightResult = {preflight: null, loading: false, error: false};
const LOADING: PhaseDeliverablePreflightResult = {preflight: null, loading: true, error: false};
const FAILED: PhaseDeliverablePreflightResult = {preflight: null, loading: false, error: true};

/**
 * The deliverable approval pre-flight for one phase of `projectId`, read when the send
 * sign-off modal opens. A supplementary read for one dialog, which is why it is the thin
 * imperative fetch rather than a view-loading query.
 *
 * Two reads: the project's assets, then the phase of each task that has a deliverable.
 * A project without deliverables never makes the second one.
 */
export const usePhaseDeliverablePreflight = (
	projectId: string | null | undefined,
	phaseId: string | null | undefined
): PhaseDeliverablePreflightResult => {
	const {fetch: fetchAssets} = useForecastFetchQuery<
		usePhaseDeliverablePreflightAssetsQuery['response'],
		usePhaseDeliverablePreflightAssetsQuery['variables']
	>(assetsQuery);
	const {fetch: fetchTasks} = useForecastFetchQuery<
		usePhaseDeliverablePreflightTasksQuery['response'],
		usePhaseDeliverablePreflightTasksQuery['variables']
	>(tasksQuery);
	const [result, setResult] = useState<PhaseDeliverablePreflightResult>(IDLE);

	useEffect(() => {
		if (!projectId || !phaseId) {
			setResult(IDLE);
			return undefined;
		}
		// A response for a project or phase this hook has since moved off is dropped.
		let current = true;
		const settle = (next: PhaseDeliverablePreflightResult) => {
			if (current) setResult(next);
		};
		setResult(LOADING);
		fetchAssets(
			{projectId},
			assetsData => {
				const assets = assetsData?.viewer?.project?.clientAssets as ReadonlyArray<PreflightAsset> | null | undefined;
				if (!assets) {
					settle(FAILED);
					return;
				}
				const taskIds = deliverableTaskGlobalIds(assets);
				if (taskIds.length === 0) {
					settle({preflight: {kind: 'none'}, loading: false, error: false});
					return;
				}
				fetchTasks(
					{taskIds, first: taskIds.length},
					tasksData => {
						const tasks: PreflightTask[] = (tasksData?.viewer?.tasks?.edges ?? []).flatMap(edge =>
							edge?.node ? [{id: edge.node.id, phase: edge.node.phase ? {id: edge.node.phase.id} : null}] : []
						);
						settle({
							preflight: derivePhaseDeliverablePreflight(phaseId, tasks, assets),
							loading: false,
							error: false,
						});
					},
					() => settle(FAILED)
				);
			},
			() => settle(FAILED)
		);
		return () => {
			current = false;
		};
	}, [projectId, phaseId]);

	return result;
};
